/**
 * NOTED CONFLICTS (V33 revised, Tom 2026-10-26: "allow it, but be loud about
 * the temproray inconsistency - and warn when doing it"). In a draft or a
 * proposal, a deliberate link or pin change may leave a route conflict; the
 * write goes through and the conflict is recorded as NOTED. It is shown
 * loudly until it is settled — aligned, or kept as a deliberate override —
 * and commit, proposal accept and merge are refused while any is only
 * noted. Reads that depend on one answer "incomplete: conflicting pins".
 *
 * Each noted conflict is a RouteConflict (VC-D's V33 shape: key, island,
 * name, pinned, routes, align, reason), with the lane it stands in.
 *
 * Kept per holder: what this page's own writes noted, with the server's
 * sentence; the page's alert also reads the open's `pin_conflicts`
 * (`notedFromAlert`). Settling one forgets it.
 */

import { useSyncExternalStore } from "react";
import type { RouteConflict } from "../versioning-client";
import { conflictName, isHidden, type PinConflict } from "./pin-conflicts";

/**
 * Every server name this client reads or sends for noted conflicts, kept
 * here only. CONFIRMED: VC-D, vc-d/v33 (ef0aa620c4 and its answers of
 * 2026-10-26). STUB: not served yet — a placeholder VC-D was asked for.
 */
export const NOTED_WIRE = {
  /** CONFIRMED: a write that LANDED — pin create/update, picker apply, undo
   *  (confirm), PUT /api/create, PATCH /api/modify: the conflicts it left
   *  noted (VC-D's ConflictPreview, as a preview's `route_conflicts`) and the
   *  one loud sentence; both absent (modify: [] and null) when none */
  writeField: "noted_conflicts",
  warningField: "warning",
  /** CONFIRMED (ba09c01fdc): a draft's /diff and an open proposal's
   *  changes: the conflicts noted there, each with `branch`. The PAGE knows
   *  them from the open's `pin_conflicts`: `chosen` set, `settled: false` */
  reviewField: "noted_conflicts",
  /** CONFIRMED: 409 route_conflict, its conflicts in `detail.conflicts`;
   *  a commit / accept / merge refusal's each carry `branch`, the lane to
   *  settle it in; one with none is what a change would leave */
  refusalCode: "route_conflict",
  refusalField: "conflicts",
  /** CONFIRMED: a report's `incomplete_json.missing[]` entry for a noted
   *  conflict, its sentences (each names the conflict) in `details` */
  reportMissingCode: "pin_conflict_noted",
  reportDetailsField: "details",
  /** CONFIRMED: GET …/calculations/{expr}?lane= answers
   *  {status: "incomplete", code: "pin_conflict_noted", reason}; every
   *  DerivedValue read (9c61fb1c88) `calculation_status: "incomplete"`,
   *  `calculation_message` the sentence, `value: null` */
  calculationStatus: "incomplete",
  calculationCode: "pin_conflict_noted",
  /** CONFIRMED (ba09c01fdc): Keep as override for a pin in a noted
   *  conflict, POST {branch, review_token}, answered like an update; the pin
   *  is the conflict's `pin_id` (every readable one carries it), else the
   *  listing's pin of that island; the review token is always the listing's
   *  (whose read retries a busy moment after a pin change: referencePins) */
  keepPath: (holder: string, pin: string) => `${holder}/reference-pins/${encodeURIComponent(pin)}/keep`,
} as const;

export type NotedConflict = RouteConflict & {
  /** the draft or proposal lane it stands in, when the server says */
  branch?: string;
  /** the holder it belongs to, when the server says (a refusal may name another's) */
  holder?: string;
  /** this project's own pin of that island (VC-D: on each readable conflict) */
  pin_id?: string;
};

const short = (c: string) => c.slice(0, 10);

/** A conflict in the page's alert (`pin_conflicts`) that is only NOTED —
 *  this project's own pin chosen, not kept on purpose — as a noted conflict:
 *  its other versions are the routes, and Align is offered when they agree
 *  on one. Null for any other (ambiguous, settled, or an island the reader
 *  cannot see, which the alert itself says). */
export function notedFromAlert(c: PinConflict): NotedConflict | null {
  if (!c.chosen || c.settled !== false || isHidden(c.island)) return null;
  const name = conflictName(c);
  const others = c.versions.filter((v) => v.commit !== c.chosen);
  const commits = Array.from(new Set(others.map((v) => v.commit)));
  const called = name ?? "this item";
  return {
    key: c.island, island: c.island, name, pinned: c.chosen,
    routes: others.map((v) => ({ commit: v.commit, ...(v.name ? { name: v.name } : {}), via: v.via })),
    align: commits.length === 1 ? { island: c.island, commit: commits[0] } : null,
    reason: `This project uses ${called} at ${short(c.chosen)} while another of its references uses ${called} at ${commits.map(short).join(", ")}.`,
  };
}

const isConflict = (c: unknown): c is NotedConflict => {
  const x = c as NotedConflict;
  return !!x && typeof x === "object" && typeof x.key === "string" && !!x.key && typeof x.reason === "string" && Array.isArray(x.routes ?? []);
};

/** The noted conflicts in an answer's `field` (none: []). */
export function notedIn(answer: unknown, field: string): NotedConflict[] {
  const list = answer && typeof answer === "object" ? (answer as Record<string, unknown>)[field] : null;
  return Array.isArray(list) ? list.filter(isConflict).map((c) => ({ ...c, routes: c.routes ?? [] })) : [];
}

const byHolder = new Map<string, NotedConflict[]>();
/** the server's sentence from the holder's latest write that noted one */
const warnings = new Map<string, string>();
const listeners = new Set<() => void>();
const NONE: NotedConflict[] = [];
const notify = () => { for (const l of Array.from(listeners)) l(); };

const keyOf = (c: NotedConflict) => `${c.branch ?? ""}\u0000${c.key}`;

function put(holder: string, list: NotedConflict[]) {
  if (list.length) byHolder.set(holder, list);
  else byHolder.delete(holder);
  notify();
}

/** The holder's final release: what its writes noted goes (its next open's
 *  `pin_conflicts` says what still stands). */
export function forgetNotedConflicts(holder: string): void {
  warnings.delete(holder);
  if (byHolder.has(holder)) put(holder, []);
}

/** The server's one loud sentence on a write that noted a conflict. */
export function warningIn(answer: unknown): string | null {
  const w = answer && typeof answer === "object" ? (answer as Record<string, unknown>)[NOTED_WIRE.warningField] : null;
  return typeof w === "string" && w.trim() ? w : null;
}

/** A write's answer: the conflicts it noted join the holder's (by key), and
 *  are returned for the write's own warning. */
export function noteWrite(holder: string, answer: unknown): NotedConflict[] {
  const noted = notedIn(answer, NOTED_WIRE.writeField);
  if (!noted.length) return noted;
  const said = warningIn(answer);
  if (said) warnings.set(holder, said);
  const kept = new Map((byHolder.get(holder) ?? NONE).map((c) => [keyOf(c), c]));
  for (const c of noted) kept.set(keyOf(c), c);
  put(holder, Array.from(kept.values()));
  return noted;
}

/** One settled (aligned or kept): no longer shown. */
export function forgetNotedConflict(holder: string, conflict: NotedConflict): void {
  const cur = byHolder.get(holder);
  if (!cur) return;
  put(holder, cur.filter((c) => keyOf(c) !== keyOf(conflict)));
}

/** The latest write's warning for this holder, until dismissed or every
 *  conflict is settled. */
export function notedWarningOf(holder: string | null | undefined): string | null {
  return (holder && byHolder.has(holder) && warnings.get(holder)) || null;
}

export function dismissNotedWarning(holder: string): void {
  if (warnings.delete(holder)) notify();
}

export function useNotedWarning(holder: string | null | undefined): string | null {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => notedWarningOf(holder),
    () => null,
  );
}

export function notedConflictsOf(holder: string | null | undefined): NotedConflict[] {
  return (holder && byHolder.get(holder)) || NONE;
}

export function useNotedConflicts(holder: string | null | undefined): NotedConflict[] {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => notedConflictsOf(holder),
    () => NONE,
  );
}

export function resetNotedConflictsForTests(): void {
  byHolder.clear();
  warnings.clear();
  notify();
}
